import { sb } from './supabase';

const OPEN_TASK_STATUSES = ['open', 'in_progress', 'blocked'];

/** Manually route a build item when no assignment task exists yet (legacy / orphan items). */
export async function routeBuildItem(buildItemId) {
  const { data, error } = await sb.rpc('route_build_item', {
    p_build_item_id: buildItemId,
  });
  if (error) throw error;
  return data;
}

/** Lead completes Assignment task — creates Work task for picked team member. */
export async function completeAssignmentTask(taskId, assigneeProfileId) {
  const { data, error } = await sb.rpc('complete_assignment_task', {
    p_task_id: taskId,
    p_assignee_profile_id: assigneeProfileId,
  });
  if (error) throw error;
  return data;
}

/** Owner completes Work task — marks build item done. */
export async function completeWorkTask(taskId) {
  const { error } = await sb.rpc('complete_work_task', {
    p_task_id: taskId,
  });
  if (error) throw error;
}

/** Func consultant completes spec draft — creates SA approval task. */
export async function completeFunctionalSpecWork(taskId) {
  const { data, error } = await sb.rpc('complete_functional_spec_work', {
    p_task_id: taskId,
  });
  if (error) throw error;
  return data;
}

/** SA approves functional spec. */
export async function approveFunctionalSpec(taskId) {
  const { error } = await sb.rpc('approve_functional_spec', {
    p_task_id: taskId,
  });
  if (error) throw error;
}

/** Manually ensure functional spec + work task for a build item (backfill). */
export async function routeFunctionalSpec(buildItemId) {
  const { data, error } = await sb.rpc('route_functional_spec', {
    p_build_item_id: buildItemId,
  });
  if (error) throw error;
  return data;
}

/** Complete test script work — unit/integration pass; UAT moves to BPO approval. */
export async function completeTestScriptWork(taskId) {
  const { data, error } = await sb.rpc('complete_test_script_work', {
    p_task_id: taskId,
  });
  if (error) throw error;
  return data;
}

/** BPO signs off UAT script. */
export async function approveTestScriptUat(taskId) {
  const { error } = await sb.rpc('approve_test_script_uat', {
    p_task_id: taskId,
  });
  if (error) throw error;
}

/** Manually create integration or UAT test script (+ work task). Unit uses build-item done hook. */
export async function routeTestScript(buildItemId, testType = 'integration') {
  const { data, error } = await sb.rpc('route_test_script', {
    p_build_item_id: buildItemId,
    p_test_type: testType,
  });
  if (error) throw error;
  return data;
}

/** Log a defect and seed assignment task (build item owner). */
export async function createDefect(buildItemId, title, description = null, testScriptId = null) {
  const { data, error } = await sb.rpc('create_defect', {
    p_build_item_id: buildItemId,
    p_title: title,
    p_description: description,
    p_test_script_id: testScriptId,
  });
  if (error) throw error;
  return data;
}

/** Build owner assigns fixer — creates Work task. */
export async function completeDefectAssignment(taskId, assigneeProfileId) {
  const { data, error } = await sb.rpc('complete_defect_assignment', {
    p_task_id: taskId,
    p_assignee_profile_id: assigneeProfileId,
  });
  if (error) throw error;
  return data;
}

/** Fixer completes work — seeds Testing Lead retest/close approval. */
export async function completeDefectWork(taskId) {
  const { data, error } = await sb.rpc('complete_defect_work', {
    p_task_id: taskId,
  });
  if (error) throw error;
  return data;
}

/** Testing Lead closes defect after retest. */
export async function approveDefectRetest(taskId) {
  const { error } = await sb.rpc('approve_defect_retest', {
    p_task_id: taskId,
  });
  if (error) throw error;
}

/** Complete gate condition work task — marks condition met. */
export async function completeGateConditionTask(taskId) {
  const { error } = await sb.rpc('complete_gate_condition_task', {
    p_task_id: taskId,
  });
  if (error) throw error;
}

/** Manually seed gate condition work task (backfill). */
export async function routeGateCondition(conditionId) {
  const { data, error } = await sb.rpc('route_gate_condition', {
    p_condition_id: conditionId,
  });
  if (error) throw error;
  return data;
}

/** Reopen a met gate condition and create a fresh work task. */
export async function reopenGateCondition(conditionId) {
  const { data, error } = await sb.rpc('reopen_gate_condition', {
    p_condition_id: conditionId,
  });
  if (error) throw error;
  return data;
}

/** SA confirms disposition — completes approval task when Rex proposed links. */
export async function confirmDisposition(requirementId) {
  const { error } = await sb.rpc('confirm_disposition', {
    p_requirement_id: requirementId,
  });
  if (error) throw error;
}

/** Move generated requirements into BPO review and seed approval tasks. */
export async function startBpoReview(projectId) {
  const { error } = await sb.rpc('start_bpo_review', {
    p_project_id: projectId,
  });
  if (error) throw error;
}

/** BPO accepts a project process node — completes approval tasks and advances requirements. */
export async function acceptBpoNodeReview(projectId, processNodeId) {
  const { error } = await sb.rpc('accept_bpo_node_review', {
    p_project_id: projectId,
    p_process_node_id: processNodeId,
  });
  if (error) throw error;
}

/** Load open tasks assigned to the current user (§22.7 inbox). */
export async function fetchMyOpenTasks(profileId) {
  const { data, error } = await sb
    .from('tasks')
    .select(`
      id,
      title,
      task_type,
      status,
      due_date,
      source_artefact_type,
      source_artefact_id,
      programme_id,
      project_id,
      created_at,
      projects ( name ),
      programmes ( name )
    `)
    .eq('assigned_to', profileId)
    .in('status', OPEN_TASK_STATUSES)
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Load open/in-progress tasks for build items in a project. */
export async function fetchOpenBuildItemTasks(projectId) {
  const { data, error } = await sb
    .from('tasks')
    .select('id, source_artefact_id, task_type, status, assigned_to, description, profiles!assigned_to(full_name)')
    .eq('project_id', projectId)
    .eq('source_artefact_type', 'build_item')
    .in('status', OPEN_TASK_STATUSES);
  if (error) throw error;
  return data || [];
}

/** Load open approval tasks for disposition + requirement artefacts in a project. */
export async function fetchOpenApprovalTasks(projectId) {
  const { data, error } = await sb
    .from('tasks')
    .select('id, source_artefact_id, source_artefact_type, task_type, status, assigned_to, title, profiles!assigned_to(full_name)')
    .eq('project_id', projectId)
    .eq('task_type', 'approval')
    .in('source_artefact_type', ['disposition', 'requirement'])
    .in('status', OPEN_TASK_STATUSES);
  if (error) throw error;
  return data || [];
}

/** Load open tasks for functional specs in a project. */
export async function fetchOpenFunctionalSpecTasks(projectId) {
  const { data, error } = await sb
    .from('tasks')
    .select('id, source_artefact_id, task_type, status, assigned_to, title, profiles!assigned_to(full_name)')
    .eq('project_id', projectId)
    .eq('source_artefact_type', 'functional_spec')
    .in('status', OPEN_TASK_STATUSES);
  if (error) throw error;
  return data || [];
}

/** Load open tasks for test scripts in a project. */
export async function fetchOpenTestScriptTasks(projectId) {
  const { data, error } = await sb
    .from('tasks')
    .select('id, source_artefact_id, task_type, status, assigned_to, title, profiles!assigned_to(full_name)')
    .eq('project_id', projectId)
    .eq('source_artefact_type', 'test_script')
    .in('status', OPEN_TASK_STATUSES);
  if (error) throw error;
  return data || [];
}

/** Load open tasks for defects in a project. */
export async function fetchOpenDefectTasks(projectId) {
  const { data, error } = await sb
    .from('tasks')
    .select('id, source_artefact_id, task_type, status, assigned_to, title, profiles!assigned_to(full_name)')
    .eq('project_id', projectId)
    .eq('source_artefact_type', 'defect')
    .in('status', OPEN_TASK_STATUSES);
  if (error) throw error;
  return data || [];
}

/** Load open tasks for gate conditions (project-scoped). */
export async function fetchOpenGateConditionTasks(projectIds) {
  if (!projectIds?.length) return [];
  const { data, error } = await sb
    .from('tasks')
    .select('id, source_artefact_id, task_type, status, assigned_to, title, profiles!assigned_to(full_name), project_id')
    .in('project_id', projectIds)
    .eq('source_artefact_type', 'gate_condition')
    .in('status', OPEN_TASK_STATUSES);
  if (error) throw error;
  return data || [];
}
